import { buildDefaultTimeRangeStreams } from '../../../widgets/timeRangeParts';
import { buildDefaultEnvironmentalStatusMetrics } from '../../../widgets/environmentalStatusConfig';
import { normalizeDashboardWidgetFontSizes } from '../../../utils/dashboardWidgetFontSizes';

const DEFAULT_PROP_KEY_TO_WIDGET_TYPE = {
  defaultChartProps: 'chart',
  defaultPeriodColumnProps: 'period-column',
  previewPeriodColumnProps: 'period-column',
  defaultGaugeProps: 'gauge',
  defaultLiquidProps: 'liquid',
  defaultWaterTankProps: 'water-tank',
  defaultRadialGaugeProps: 'radial-gauge',
  defaultLabelProps: 'label',
  defaultLEDProps: 'led',
  defaultTextTitleProps: 'text-title',
  defaultCameraStreamProps: 'camera-stream',
  defaultTimeRangeProps: 'time-range',
  defaultEnvironmentalStatusProps: 'environmental-status',
  defaultNumberProps: 'number',
  defaultSliderProps: 'slider',
  defaultSwitchProps: 'switch',
  defaultPushButtonProps: 'push-button',
  defaultTerminalProps: 'terminal',
  defaultKeypadProps: 'keypad',
  defaultMenuProps: 'menu',
  defaultSegmentSwitchProps: 'segment-switch',
  defaultImageButtonProps: 'image-button',
  defaultTextProps: 'text',
  defaultTableProps: 'table',
  defaultFanProps: 'fan',
  defaultPipeProps: 'pipe',
  defaultElbowProps: 'elbow',
  defaultWaterPumpProps: 'water-pump',
  defaultAssetStatusProps: 'asset-status',
  defaultStatusArrowProps: 'status-arrow',
};

const applyWidgetFontSizeDefaults = (defaults, widgetFontSizeConfig) => {
  const widgetFontSizes = normalizeDashboardWidgetFontSizes(widgetFontSizeConfig).widgets;

  return Object.entries(defaults).reduce((acc, [key, value]) => {
    const widgetType = DEFAULT_PROP_KEY_TO_WIDGET_TYPE[key];
    acc[key] = widgetType && value && typeof value === 'object' && !Array.isArray(value)
      ? { ...value, ...(widgetFontSizes[widgetType] || {}) }
      : value;
    return acc;
  }, {});
};

export const getWebDashboardWidgetDefaults = (t = {}, widgetFontSizeConfig = {}) => {
  const defaultChartProps = {
    chart_type: 'line',
    freeMovement: true,
    showUnit: true,
    xData: ['May', 'Jun', 'Jul', 'Aug'],
    curves: [{
      yData: [3, 4, 5, 6],
      customize: false,
      min: 0,
      max: 0,
      yLabel: 'Label',
      pin: 'Pin',
      aggregation: 'mean',
      bgColor: '#eb1518',
      hbgColor: '#fc2629',
      bdColor: '#030ae5',
    }],
  };

  const defaults = {
    defaultChartProps,
    defaultPeriodColumnProps: {
      period: 'day',
      stat: 'max',
      freeMovement: true,
      showUnit: true,
      color: '#3b82f6',
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
      xData: [],
      yData: [],
    },
    previewPeriodColumnProps: {
      period: 'day',
      stat: 'max',
      freeMovement: true,
      showUnit: true,
      color: '#3b82f6',
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
      xData: [
        '2025-01-01T00:00:00.000Z',
        '2025-01-02T00:00:00.000Z',
        '2025-01-03T00:00:00.000Z',
        '2025-01-04T00:00:00.000Z',
      ],
      yData: [18, 24, 16, 27],
    },
    defaultGaugeProps: {
      value: 50,
      freeMovement: true,
      factor: 1,
      precision: 1,
      showUnit: true,
      color: '#FF928A',
      unfilledColor: '',
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
    },
    defaultLiquidProps: {
      min: 0,
      max: 100,
      value: 50,
      showUnit: true,
      unit: '',
      color: '#74C7FF',
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
      cardBackgroundColor: 'var(--header-background)',
      titleColor: null,
      valueColor: null,
      unitColor: null,
      glowColor: 'rgba(116, 199, 255, 0.52)',
    },
    defaultWaterTankProps: {
      min: 0,
      max: 100,
      value: 50,
      showUnit: true,
      unit: '',
      color: '#72B8F4',
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
      cardBackgroundColor: 'var(--header-background)',
      titleColor: null,
      valueColor: null,
      unitColor: null,
      glowColor: 'rgba(114, 184, 244, 0.46)',
      linkToNearby: true,
    },
    defaultRadialGaugeProps: {
      value: 50,
      freeMovement: true,
      factor: 1,
      precision: 1,
      showUnit: true,
      color: '#FF928A',
      unfilledColor: '',
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
    },
    defaultLabelProps: {
      value: 50,
      factor: 1,
      precision: 1,
      alignment: 'left',
      showUnit: true,
      showValueLine: true,
      color: '#ff00ff',
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
      labelShadowEnabled: false,
      labelShadowBlur: 28,
      labelShadowStrength: 2,
      labelShadowUseValueColor: true,
      labelShadowColor: '#00f0f0',
      labelShadowChangeColorBasedOnValue: false,
      labelShadowColorGrading: false,
      labelShadowColors: [],
    },
    defaultLEDProps: { color: '#00f0f0', value: 50 },
    defaultTextTitleProps: {
      title: t.widgetTitles?.textTitle || 'Title',
      titleSize: 20,
      color: '#00f0f0',
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
      alignment: 'left',
      fontFamily: '',
      fontWeight: '',
      fontStyle: 'normal',
      textDecoration: 'none',
    },
    defaultTimeRangeProps: {
      streams: buildDefaultTimeRangeStreams(),
      showLeadingZeroHours: true,
      showLeadingZeroMinutes: true,
      borderRadius: 20,
      titleSize: 20,
      valueSize: 64,
    },
    defaultEnvironmentalStatusProps: {
      metrics: buildDefaultEnvironmentalStatusMetrics(),
      freeMovement: true,
      showControlSwitch: true,
      controlDatastream: {
        deviceId: 0,
        dataId: null,
        pin: '',
        defaultValue: 0,
      },
      borderRadius: 20,
      titleSize: 20,
      titleColor: '#ffffff',
      datastreamTitleSize: 16,
      valueSize: 31,
    },
    defaultNumberProps: {
      visualStyle: 'glass',
      min: 0,
      max: 100,
      step: 1,
      value: 50,
      freeMovement: true,
      showUnit: false,
      showMinMax: false,
      loopValues: false,
      contentAlignment: 'auto',
      color: '#3ccfe6',
      buttonBgColor: 'rgba(34, 88, 113, 0.48)',
      buttonHoverBgColor: 'rgba(49, 119, 146, 0.68)',
      valueColor: null,
      valueHoverColor: null,
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
    },
    defaultSliderProps: {
      visualStyle: 'glass',
      step: 1,
      value: 50,
      showMinMax: false,
      freeMovement: true,
      color: '#4489b6',
      buttonTextColor: '#3ccfe6',
      buttonBgColor: 'rgba(34, 88, 113, 0.48)',
      valueColor: null,
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
      direction: 'ltr',
      contentAlignment: 'auto',
    },
    defaultSwitchProps: {
      color: '#C52184',
      inactiveColor: '#D1D1D6',
      onLabel: 'On',
      offLabel: 'Off',
      value: 50,
      showLabel: false,
      alignment: 'left',
      switchSize: 28,
    },
    defaultPushButtonProps: {
      color: '#4FC4D3',
      inactiveColor: 'rgba(79, 196, 211, 0.48)',
      textColor: '#FFFFFF',
      inactiveTextColor: '#8CE7EF',
      onLabel: 'on',
      offLabel: 'off',
      value: 0,
      showLabel: true,
      alignment: 'start',
      interactionMode: 'push',
      buttonSize: 16,
      valueFontSize: 16,
    },
    defaultTerminalProps: {
      hint: 'Type Here...',
      newLine: false,
      autoScroll: false,
      value: '< Enabled',
      bgColor: 'rgb(0, 0, 0)',
      fgColor: 'rgb(255, 255, 255)',
    },
    defaultKeypadProps: {
      freeMovement: true,
      buttonColor: 'rgba(114, 118, 128, 0.18)',
      textColor: 'rgb(255, 255, 255)',
      inputTextColor: 'rgba(255, 255, 255, 0.9)',
      inputBackgroundColor: 'rgba(25, 38, 61, 0.86)',
      keyFontSize: 18,
    },
    defaultMenuProps: {
      visualStyle: 'glass',
      alignment: 'left',
      options: [{
        label: 'Option 0',
        value: 0,
      }, {
        label: 'Option 1',
        value: 1,
      }],
    },
    defaultSegmentSwitchProps: {
      visualStyle: 'glass',
      freeMovement: true,
      activeBackgroundColor: 'rgba(60, 159, 181, 0.82)',
      activeTextColor: '#ffffff',
      inactiveTextColor: 'rgba(204, 210, 222, 0.78)',
      inactiveBackgroundColor: 'rgba(19, 30, 51, 0.92)',
      fontSize: 16,
      paddingLeft: 8,
      paddingRight: 8,
      paddingTop: 8,
      paddingBottom: 8,
      options: [{
        label: 'Option 0',
        value: 0,
      }, {
        label: 'Option 1',
        value: 1,
      }],
    },
    defaultImageButtonProps: {
      images: [{
        imageUrl: null,
        file: null,
        value: 0,
      }, {
        imageUrl: null,
        file: null,
        value: 1,
      }],
    },
    defaultBackgroundImageProps: {
      imageUrl: null,
      file: null,
    },
    defaultTextProps: { hint: 'Type Here...', value: 50, freeMovement: true, visualStyle: 'glass' },
    defaultTableProps: {
      freeMovement: true,
      xData: defaultChartProps.xData,
      curves: defaultChartProps.curves,
      headerBgColor: 'var(--header-background)',
      headerTextColor: 'var(--header-color)',
      bodyBgColor: 'var(--cell-background)',
      bodyTextColor: 'var(--cell-color)',
    },
    defaultFanProps: {
      datastreams: [],
      maxSpeed: 10,
      showLabel: true,
      alignment: 'center',
    },
    defaultPipeProps: {
      color: '#76B6EA',
      pipeColor: 'rgba(93, 133, 160, 0.78)',
      pipeBackgroundColor: 'rgba(18, 28, 40, 0.55)',
      onLabel: 'Water Flowing',
      offLabel: 'No Flow',
      value: 0,
      showLabel: true,
      direction: 'horizontal',
      flowDirection: 'left-to-right',
      frameBg: 'rgba(23, 37, 64, 0.96)',
      frameBorderColor: 'rgba(24, 99, 129, 0.76)',
      frameBorderWidth: 2,
      frameRadius: 18,
      framePadding: 0,
      pipeHeight: 70,
      pipeWidthPercent: 100,
      pipeBorderColor: 'rgba(156, 194, 216, 0.42)',
      pipeBorderWidth: 10,
      pipeRadius: 18,
      pipeGlassColor: 'rgba(142, 190, 214, 0.44)',
      clampCount: 4,
      clampColor: 'rgba(118, 152, 153, 0.92)',
      clampWidth: 24,
      clampRadius: 16,
      waterOpacity: 0.85,
      waveSpeed: 2.75,
      titleFontSize: 34,
      statusFontSize: 30,
      linkToNearby: true,
    },
    defaultElbowProps: {
      color: '#76B6EA',
      pipeColor: 'rgba(93, 133, 160, 0.78)',
      pipeBackgroundColor: 'rgba(18, 28, 40, 0.55)',
      pipeGlassColor: 'rgba(142, 190, 214, 0.44)',
      pipeBorderColor: 'rgba(156, 194, 216, 0.42)',
      clampColor: 'rgba(118, 152, 153, 0.92)',
      pipeHeight: 70,
      onLabel: 'Water Flowing',
      offLabel: 'No Flow',
      value: 0,
      showLabel: true,
      direction: 'top-right',
      titleFontSize: 34,
      statusFontSize: 30,
      linkToNearby: true,
    },
    defaultWaterPumpProps: {
      color: '#76B6EA',
      pipeColor: 'rgba(93, 133, 160, 0.78)',
      pipeBackgroundColor: 'rgba(18, 28, 40, 0.55)',
      pipeGlassColor: 'rgba(142, 190, 214, 0.44)',
      pipeBorderColor: 'rgba(156, 194, 216, 0.42)',
      clampColor: 'rgba(118, 152, 153, 0.92)',
      pipeHeight: 70,
      onLabel: 'Water Flowing',
      offLabel: 'No Flow',
      value: 0,
      showLabel: false,
      direction: 'left-to-right',
      linkToNearby: true,
    },
    defaultBoxWidgetProps: {
      backgroundType: 'gradient',
      backgroundColor: 'rgba(18, 28, 40, 0.9)',
      gradientAngle: 135,
      gradientColors: ['rgba(56, 74, 94, 0.95)', 'rgba(22, 31, 45, 0.95)'],
      opacity: 1,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.08)',
      shadowEnabled: true,
      shadowOffsetX: 0,
      shadowOffsetY: 10,
      shadowBlur: 30,
      shadowSpread: 0,
      shadowColor: 'rgba(0, 0, 0, 0.35)',
      backdropBlur: 0,
    },
    defaultCameraStreamProps: {
      url: '',
      freeMovement: true,
      showPowerToggle: true,
      powerTogglePosition: 'bottom-right',
    },
    defaultAssetStatusProps: {
      freeMovement: true,
      assetName: 'Name of Assets',
      rows: [
        { dataId: null, pin: '', label: 'Status', showUnit: true },
        { dataId: null, pin: '', label: 'Text', showUnit: true },
        { dataId: null, pin: '', label: 'Text', showUnit: true },
      ],
      iconColor: '#f31f28',
      iconDataId: null,
      iconPin: '',
      iconGradientEnabled: false,
      iconGradientDataId: null,
      iconGradientPin: '',
      iconGradientColorGrading: true,
      iconGradientColors: [
        { value: 0, color: '#f31f28' },
        { value: 100, color: '#00d26a' },
      ],
      iconRules: [
        { dataId: null, pin: '', operator: 'eq', equals: 'ON', color: '#00d26a', animation: 'pulse' },
        { dataId: null, pin: '', operator: 'eq', equals: 'RUNNING', color: '#00d26a', animation: 'glow' },
        { dataId: null, pin: '', operator: 'eq', equals: 'OFF', color: '#f31f28', animation: 'blink' },
      ],
      iconDefaultAnimation: 'pulse',
      borderEnabled: false,
      borderWidth: 1,
      titleFontSize: 14,
      assetNameFontSize: 14,
      datastreamLabelFontSize: 14,
      valueFontSize: 16,
    },
    defaultStatusArrowProps: {
      freeMovement: true,
      shape: 'elbow',
      startAnchor: 'left',
      endAnchor: 'right',
      color: '#26b4ff',
      strokeWidth: 3,
      dash: 'none',
      bend: 50,
      showStartDot: true,
      showEndArrow: true,
      reverse: false,
      animated: false,
      changeColorBasedOnValue: false,
      colorGrading: false,
      colors: [],
      statusRules: [],
    },
  };

  return applyWidgetFontSizeDefaults(defaults, widgetFontSizeConfig);
};
